import {
  Button,
  Flex,
  Paper,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBolt,
  IconBuilding,
  IconChartDonut,
  IconMap2,
  IconMapPin,
  IconUserPlus,
  IconWorld,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { NAV_COMPANY_SIGNUP_PATH } from "@/shared/constants/navAuthPaths.const";
import { useGuestNetworkStats } from "../hooks/useGuestNetworkStats";
import { useDiscoverHeroIsDesktop, useDiscoverHeroIsPhone } from "../hooks/useDiscoverHeroDonutMetrics";
import DiscoverHeroWhoAmIDonut from "./hero/DiscoverHeroWhoAmIDonut";
import DiscoverHeroGeographyDonut from "./hero/DiscoverHeroGeographyDonut";
import DiscoverHeroIndustryBars from "./hero/DiscoverHeroIndustryBars";
import "./DiscoverActiveCollaborationHero.css";

const NAVY = "#04305c";

type StatCardProps = {
  icon: React.ReactNode;
  iconBg: string;
  value: string;
  label: string;
};

function StatCard({ icon, iconBg, value, label }: StatCardProps) {
  return (
    <Paper
      radius="md"
      px="xs"
      py={6}
      withBorder
      style={{
        borderColor: "#E2E8F0",
        backgroundColor: "#F8FBFF",
        flex: "0 0 auto",
        minHeight: 34,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <Flex align="center" gap={8} w="100%" wrap="nowrap">
        <ThemeIcon size={28} radius="md" style={{ backgroundColor: iconBg, color: "#FFFFFF", flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          <Text fw={800} fz={16} c={NAVY} lh={1.1}>
            {value}
          </Text>
          <Text fw={600} size="xs" c="#475569" lh={1.2}>
            {label}
          </Text>
        </Stack>
      </Flex>
    </Paper>
  );
}

type SectionHeaderProps = {
  color?: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
};

function SectionHeader({ color, icon, title, subtitle }: SectionHeaderProps) {
  const isDesktop = useDiscoverHeroIsDesktop();
  return (
    <Stack gap={isDesktop ? 4 : 1} mb={isDesktop ? 6 : 4} style={{ flexShrink: 0 }}>
      <Flex align="flex-start" gap={8} wrap="nowrap">
        <ThemeIcon
          size={isDesktop ? 32 : 28}
          radius="sm"
          style={{ backgroundColor: color ?? NAVY, flexShrink: 0 }}
        >
          {icon}
        </ThemeIcon>
        <Text fw={800} size={isDesktop ? "md" : "sm"} c={NAVY} lh={1.25} style={{ minWidth: 0, flex: 1 }}>
          {title}
        </Text>
      </Flex>
      <Text size={isDesktop ? "sm" : "xs"} c="#64748B" lh={1.35} pl={isDesktop ? 40 : 36}>
        {subtitle}
      </Text>
    </Stack>
  );
}

type DiscoverActiveCollaborationHeroProps = {
  /** Guest landing shows intro + CTAs; auth Explore shows only the three stats panels. */
  variant?: "guest" | "auth";
  onExploreAll?: () => void;
};

/** Hero fills the parent Discover content Container (shared width with tabs/results). */
export default function DiscoverActiveCollaborationHero({
  variant = "guest",
  onExploreAll,
}: DiscoverActiveCollaborationHeroProps) {
  const showIntro = variant === "guest";
  const isPhone = useDiscoverHeroIsPhone();
  const statLayout = isPhone ? "column" : "row";
  const { stats, loading, loadError } = useGuestNetworkStats(true);

  const continentsValue = stats ? String(stats.continentsCovered) : "—";
  const countryCount = stats?.totalCountries ?? 0;

  return (
    <Paper
      className={`discover-hero-shell${showIntro ? "" : " discover-hero-shell--auth"}`}
      radius={24}
      w="100%"
      style={
        showIntro
          ? {
              background: "linear-gradient(180deg, #EAF4FB 0%, #F8FBFF 50%, #FFFFFF 100%)",
              border: "1px solid #D6E8F5",
              overflow: "visible",
            }
          : {
              background: "#F8FBFF",
              border: "1px solid #E2E8F0",
              overflow: "visible",
            }
      }
    >
      <div className="discover-hero-scene">
        <div className="discover-hero-sticky">
          <div className="discover-hero-panels">
            {showIntro ? (
              <div className="discover-hero-panel discover-hero-panel-intro">
                <SectionHeader
                  color={NAVY}
                  icon={<IconBolt size={16} color="#FBBF24" fill="#FBBF24" />}
                  title="Active Collaboration Opportunities"
                  subtitle="Verified companies looking for partners and collaborators."
                />
                <div className="discover-hero-panel-body">
                  <Stack gap={4} style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
                    {loading ? (
                      <>
                        <Skeleton height={34} radius="md" />
                        <Skeleton height={34} radius="md" />
                      </>
                    ) : (
                      <>
                        <StatCard
                          icon={<IconWorld size={20} />}
                          iconBg="#22C55E"
                          value={continentsValue}
                          label="Continents Covered"
                        />
                        <StatCard
                          icon={<IconMapPin size={20} />}
                          iconBg="#3B82F6"
                          value={countryCount > 0 ? String(countryCount) : "—"}
                          label="Countries Represented"
                        />
                      </>
                    )}
                  </Stack>

                  <Stack className="discover-hero-intro-actions" gap={4} w="100%" pt={4} style={{ flexShrink: 0 }}>
                    <Button
                      color={NAVY}
                      radius="md"
                      size="compact-xs"
                      fullWidth
                      onClick={onExploreAll}
                      styles={{
                        root: {
                          fontWeight: 700,
                          height: "auto",
                          minHeight: 30,
                          paddingBlock: 6,
                          paddingInline: 8,
                          overflow: "visible",
                          "&:hover": { backgroundColor: "#032847" },
                        },
                        inner: {
                          overflow: "visible",
                          height: "auto",
                          alignItems: "center",
                        },
                        label: {
                          whiteSpace: "nowrap",
                          overflow: "visible",
                          textAlign: "center",
                          fontSize: 11,
                          lineHeight: 1.2,
                        },
                      }}
                    >
                      Explore Opportunities
                    </Button>
                    <Button
                      component={Link}
                      to={NAV_COMPANY_SIGNUP_PATH}
                      variant="outline"
                      radius="md"
                      size="compact-xs"
                      fullWidth
                      color={NAVY}
                      leftSection={<IconUserPlus size={12} />}
                      styles={{
                        root: {
                          fontWeight: 700,
                          height: "auto",
                          minHeight: 30,
                          paddingBlock: 6,
                          paddingInline: 8,
                          overflow: "visible",
                          borderColor: NAVY,
                          color: NAVY,
                          backgroundColor: "#FFFFFF",
                          "&:hover": { backgroundColor: "#F8FAFC" },
                        },
                        inner: {
                          overflow: "visible",
                          height: "auto",
                          alignItems: "center",
                        },
                        section: { marginInlineEnd: 4, overflow: "visible" },
                        label: {
                          whiteSpace: "nowrap",
                          overflow: "visible",
                          textAlign: "center",
                          fontSize: 11,
                          lineHeight: 1.2,
                        },
                      }}
                    >
                      Create Free Profile
                    </Button>
                  </Stack>
                </div>
              </div>
            ) : null}

            <div className="discover-hero-stack">
              <div className="discover-hero-panel discover-hero-panel-composition">
                <SectionHeader
                  color="#7C3AED"
                  icon={<IconChartDonut size={16} color="#FFFFFF" />}
                  title="Company Network Composition"
                  subtitle="Types of business partners available for exploration."
                />
                <div className="discover-hero-panel-body">
                  {loading ? (
                    <Skeleton height="100%" radius="md" style={{ flex: 1, minHeight: 140 }} />
                  ) : (
                    <DiscoverHeroWhoAmIDonut
                      segments={stats?.whoAmI ?? []}
                      loadError={loadError}
                      layout={statLayout}
                    />
                  )}
                </div>
              </div>

              <div className="discover-hero-panel discover-hero-panel-industry">
                <SectionHeader
                  color="#16A34A"
                  icon={<IconBuilding size={16} color="#FFFFFF" />}
                  title="Companies by Industry"
                  subtitle="Discover capabilities across major industrial sectors."
                />
                <div className="discover-hero-panel-body">
                  {loading ? (
                    <Skeleton height="100%" radius="md" style={{ flex: 1, minHeight: 150 }} />
                  ) : (
                    <DiscoverHeroIndustryBars
                      industries={stats?.industries ?? []}
                      loadError={loadError}
                      layout={statLayout}
                    />
                  )}
                </div>
              </div>

              <div className="discover-hero-panel discover-hero-panel-geography">
                <SectionHeader
                  color="#2563EB"
                  icon={<IconMap2 size={16} color="#FFFFFF" />}
                  title="Global Company Distribution"
                  subtitle="Share of verified companies by headquarters region."
                />
                <div className="discover-hero-panel-body">
                  {loading ? (
                    <Skeleton height="100%" radius="md" style={{ flex: 1, minHeight: 140 }} />
                  ) : (
                    <DiscoverHeroGeographyDonut
                      segments={stats?.regions ?? []}
                      loadError={loadError}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Paper>
  );
}
